const accordionSelector = '[data-page-content-accordion]';
const terminalPlusPattern = /\+\s*$/u;
const sectionBoundarySelector = 'h2, h3';
const policyDefinitionPattern = /^\s*\d+\.\s*/u;
const internationalHeadingPattern = /^INTERNATIONAL(?:\s+ONLY)?$/iu;
const internationalPreparationPattern =
  /^INTERNATIONAL\s*[-–—]\s*HOW TO PREPARE YOUR PARCEL FOR RETURN$/iu;

function normalizeText(value) {
  return value.replace(/\s+/gu, ' ').trim();
}

function getLabelElement(element) {
  if (
    element.matches('h2, h3, h4, h5, h6') &&
    terminalPlusPattern.test(element.textContent || '')
  ) {
    return element;
  }

  if (!element.matches('p')) return null;

  return (
    Array.from(element.children).find(
      (child) => child.matches('strong') && terminalPlusPattern.test(child.textContent || '')
    ) || null
  );
}

function trimEmptyEdges(element) {
  element.querySelectorAll('meta[charset]').forEach((meta) => meta.remove());

  while (element.firstChild) {
    const firstChild = element.firstChild;
    const isWhitespace = firstChild.nodeType === Node.TEXT_NODE && !firstChild.textContent.trim();
    const isEmptyBreak = firstChild.nodeType === Node.ELEMENT_NODE && firstChild.matches('br, meta');
    if (!isWhitespace && !isEmptyBreak) break;
    firstChild.remove();
  }

  while (
    element.lastChild?.nodeType === Node.TEXT_NODE &&
    !element.lastChild.textContent.trim()
  ) {
    element.lastChild.remove();
  }
}

function hasContent(element) {
  return Boolean(
    element.textContent.trim() ||
      element.querySelector('a, audio, hr, iframe, img, ol, table, ul, video')
  );
}

function cloneMarkerRemainder(source, labelElement) {
  if (source === labelElement) return null;

  const labelIndex = Array.from(source.childNodes).indexOf(labelElement);
  if (labelIndex < 0) return null;

  const remainder = source.cloneNode(true);
  remainder.childNodes[labelIndex]?.remove();
  trimEmptyEdges(remainder);
  return hasContent(remainder) ? remainder : null;
}

function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function createAccordion(container, items, iconTemplate) {
  const document = container.ownerDocument;
  const accordion = document.createElement('component-accordion');
  const accordionId = (container.dataset.pageContentAccordionId || 'page-content').replace(
    /[^a-zA-Z0-9_-]/g,
    ''
  );

  accordion.className = 'component-accordion page-content-policy-accordion';

  items.forEach(({label, content}, index) => {
    const itemIndex = index + 1;
    const triggerId = `page-accordion-trigger-${itemIndex}-${accordionId}`;
    const panelId = `page-accordion-panel-${itemIndex}-${accordionId}`;
    const labelWrapper = document.createElement('div');
    const trigger = document.createElement('button');
    const triggerLabel = document.createElement('div');
    const triggerIndicator = document.createElement('div');
    const panel = document.createElement('div');
    const body = document.createElement('div');
    const richText = document.createElement('div');

    labelWrapper.className = 'component-accordion__label';
    labelWrapper.setAttribute('role', 'heading');
    labelWrapper.setAttribute('aria-level', '4');

    trigger.type = 'button';
    trigger.className = 'component-accordion__trigger btn-none';
    trigger.id = triggerId;
    trigger.setAttribute('data-accordion-multi', '');
    trigger.setAttribute('aria-controls', panelId);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('data-accordion-trigger', '');

    triggerLabel.className = 'component-accordion__trigger-label h7';
    triggerLabel.textContent = label;

    triggerIndicator.className = 'component-accordion__trigger-indicator';
    triggerIndicator.append(iconTemplate.content.cloneNode(true));

    panel.className = 'component-accordion__panel';
    panel.id = panelId;
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', triggerId);
    panel.setAttribute('aria-hidden', 'true');
    panel.setAttribute('inert', '');
    panel.setAttribute('data-accordion-panel', '');

    body.className = 'component-accordion__body';
    body.dataset.label = slugify(label);
    richText.className = 'rte';
    richText.append(...content);

    trigger.append(triggerLabel, triggerIndicator);
    labelWrapper.append(trigger);
    body.append(richText);
    panel.append(body);
    accordion.append(labelWrapper, panel);
  });

  accordion.addEventListener('click', (event) => {
    if (!event.target.closest('[data-accordion-trigger]')) return;
    queueMicrotask(() => syncPanelAccessibility(accordion));
  });

  return accordion;
}

function syncPanelAccessibility(accordion) {
  accordion.querySelectorAll('[data-accordion-trigger]').forEach((trigger) => {
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? accordion.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (!panel) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    panel.toggleAttribute('inert', !isExpanded);
    panel.setAttribute('aria-hidden', (!isExpanded).toString());
  });
}

function stripLeadingDefinitionNumber(element) {
  const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode) {
    if (textNode.textContent.trim()) {
      textNode.textContent = textNode.textContent.replace(policyDefinitionPattern, '');
      return;
    }
    textNode = walker.nextNode();
  }
}

function normalizePolicyDefinitions(container, children, firstMarkerIndex) {
  const tableIndex = children.findIndex(
    (element, index) => index < firstMarkerIndex && element.matches('table')
  );
  if (tableIndex < 0) return;

  const definitionElements = children
    .slice(tableIndex + 1, firstMarkerIndex)
    .filter(
      (element) =>
        element.matches('p') && policyDefinitionPattern.test(element.textContent || '')
    );
  if (definitionElements.length < 2) return;

  const list = container.ownerDocument.createElement('ol');
  list.className = 'page-content-policy-definitions';
  container.insertBefore(list, definitionElements[0]);

  definitionElements.forEach((element) => {
    const content = element.cloneNode(true);
    const item = container.ownerDocument.createElement('li');
    stripLeadingDefinitionNumber(content);
    item.append(...content.childNodes);
    list.append(item);
    element.remove();
  });

  children
    .slice(tableIndex + 1, firstMarkerIndex)
    .filter((element) => element.matches('ol') && !element.textContent.trim())
    .forEach((element) => element.remove());
}

function markPolicySpacingHeadings(container) {
  Array.from(container.children)
    .filter((element) => element.matches(sectionBoundarySelector))
    .forEach((heading) => {
      const label = normalizeText(heading.textContent || '');
      if (internationalHeadingPattern.test(label)) {
        heading.classList.add('page-content-policy__international-heading');
      }
      if (internationalPreparationPattern.test(label)) {
        heading.classList.add('page-content-policy__international-preparation');
      }
    });
}

function enhanceContainer(container) {
  if (container.dataset.pageContentAccordionEnhanced === 'true') return;

  const iconTemplateId = container.dataset.pageContentAccordionIcons;
  const iconTemplate = iconTemplateId
    ? container.ownerDocument.getElementById(iconTemplateId)
    : null;
  if (!(iconTemplate instanceof HTMLTemplateElement)) return;

  const children = Array.from(container.children);
  const markers = children
    .map((element, index) => ({element, index, labelElement: getLabelElement(element)}))
    .filter(({labelElement}) => labelElement);
  if (!markers.length) return;

  const firstMarkerIndex = markers[0].index;
  normalizePolicyDefinitions(container, children, firstMarkerIndex);
  markPolicySpacingHeadings(container);

  const sectionBoundaryIndex = children.findIndex(
    (element, index) =>
      index > firstMarkerIndex &&
      element.matches(sectionBoundarySelector) &&
      !getLabelElement(element)
  );
  const accordionEndIndex = sectionBoundaryIndex < 0 ? children.length : sectionBoundaryIndex;
  const accordionMarkers = markers.filter(({index}) => index < accordionEndIndex);

  const items = accordionMarkers.map(({element, index, labelElement}, markerIndex) => {
    const nextMarkerIndex = accordionMarkers[markerIndex + 1]?.index ?? accordionEndIndex;
    const content = [];
    const markerRemainder = cloneMarkerRemainder(element, labelElement);
    if (markerRemainder) content.push(markerRemainder);

    children
      .slice(index + 1, nextMarkerIndex)
      .forEach((contentElement) => content.push(contentElement.cloneNode(true)));

    return {
      label: (labelElement.textContent || '').replace(terminalPlusPattern, '').trim(),
      content,
    };
  });

  if (!items.length || items.some(({label}) => !label)) return;

  const accordion = createAccordion(container, items, iconTemplate);
  container.insertBefore(accordion, children[firstMarkerIndex]);
  children.slice(firstMarkerIndex, accordionEndIndex).forEach((element) => element.remove());
  container.dataset.pageContentAccordionEnhanced = 'true';
}

function enhanceWithin(root) {
  const containers = [];
  if (root instanceof Element && root.matches(accordionSelector)) containers.push(root);
  containers.push(...root.querySelectorAll(accordionSelector));
  containers.forEach(enhanceContainer);
}

function enhanceWhenReady(root) {
  if (!window.customElements) return;

  if (customElements.get('component-accordion')) {
    enhanceWithin(root);
    return;
  }

  customElements.whenDefined('component-accordion').then(() => enhanceWithin(root));
}

enhanceWhenReady(document);
document.addEventListener('shopify:section:load', (event) => enhanceWhenReady(event.target));
